import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectFabricationDataForParameters } from 'taiwan_pcb/pcb_fabrication/slice/selectors';
import { changeParameter, updateValidatorFlag } from 'taiwan_pcb/pcb_fabrication/slice/fabricationSlice';
import { doQuote } from 'taiwan_pcb/pcb_fabrication/slice/thunks';
import { validator } from 'taiwan_pcb/pcb_fabrication/slice/validators';
import { START } from 'shared/data/quoteSteps';
import InstantQuote from 'shared/components/parameters/InstantQuote';
import Quantities from 'shared/components/parameters/Quantities';
import NumberLayer from 'shared/components/parameters/NumberLayer';
import BoardWidth from 'shared/components/parameters/BoardWidth';
import BoardLength from 'shared/components/parameters/BoardLength';
import SectionPanelized from 'shared/components/parameters/section_panelized/SectionPanelized';
import MaterialThickness from 'shared/components/parameters/MaterialThickness';
import MaterialType from 'shared/components/parameters/MaterialType';
import FinishPlating from 'shared/components/parameters/FinishPlating';
import CopperWeight from 'shared/components/parameters/CopperWeight';
import Soldermask from 'shared/components/parameters/SolderMask';
import SilkScreenSides from 'shared/components/parameters/SilkscreenSides';
import SilkScreenColor from 'shared/components/parameters/SilkscreenColor';
import ViaFill from 'shared/components/parameters/ViaFill';
import ControlledImpedance from 'shared/components/parameters/ControlledImpedance';
import Castellation from 'shared/components/parameters/Castellation';
import Countersink from 'shared/components/parameters/Countersink';
import Counterbore from 'shared/components/parameters/Counterbore';
import BlindSlot from 'shared/components/parameters/BlindSlot';
import BlindBuriedHole from 'shared/components/parameters/BlindBuriedHole';
import ControlledDielectric from 'shared/components/parameters/ControlledDielectric';
import MinTraceSpace from 'shared/components/parameters/MinTraceSpace';
import SmallestHoleSize from 'shared/components/parameters/SmallestHoleSize';
import SpecialInstruction from 'shared/components/SpecialInstruction';
import SupplierSelected from 'shared/components/SupplierSelected';
import ProductQuoteNumber from 'shared/components/ProductQuoteNumber';
import ParametersChanged from 'shared/components/parameters/ParametersChanged';
import CombinedSelected from 'shared/components/parameters/CombinedSelected';
import RowSelected from 'shared/components/RowSelected';
import ColumnSelected from 'shared/components/ColumnSelected';
import VendorSelected from 'shared/components/parameters/VendorSelected';
import QuoteConfigKey from 'shared/components/parameters/QuoteConfigKey';
import EmailHidden from 'shared/components/parameters/EmailHidden';
import {
  materialType,
  finishPlating,
  cooperInner,
  cooperOuter,
} from 'taiwan_pcb/pcb_fabrication/data/selectOptions';

const Parameters = () => {
  const dispatch = useDispatch();
  const {
    quoteStep,
    parameters,
    loading,
    // eslint-disable-next-line no-unused-vars
    validatorFlag,
  } = useSelector(selectFabricationDataForParameters);

  const onChangeParameter = (name, value) => {
    dispatch(changeParameter({ name, value }));
  };

  const onQuoteRequest = () => {
    if (validator.allValid()) {
      validator.showMessages();
      dispatch(doQuote());
    } else {
      validator.showMessages();
      dispatch(updateValidatorFlag());
    }
  };

  const props = {
    parameters,
    onChangeParameter,
    disabled: loading
  };

  return (
    <div className="parameters-form">
      <div className="row">
        <div className="col-md-6">
          <h3 className="product-name">{Spree.productName}</h3>
        </div>
        <div className="col-md-6 instant-quote">
          <InstantQuote
            loading={loading}
            onQuoteRequest={onQuoteRequest}
            label={
              quoteStep !== START
                ? Spree.translations.requote
                : Spree.translations.quote
            } />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Quantities
            {...props}
            required
            errorMessage={validator.message(
              'Quantity',
              parameters.quantities,
              'group_required|group_numeric|group_min:1,num|group_max:100,num',
            )} />
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <NumberLayer {...props} required />
        </div>
        <div className="col-6">
          <MaterialThickness {...props} required />
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <BoardLength
            {...props}
            required
            errorMessage={validator.message('Board Length', parameters.board_length, 'required|numeric|min:0.1,num|max:20,num')} />
        </div>
        <div className="col-6">
          <BoardWidth
            {...props}
            required
            errorMessage={validator.message('Board Width', parameters.board_width, 'required|numeric|min:0.1,num|max:33,num')} />
        </div>
      </div>
      <div className="col-md-12">
        <div id="error_board_size" />
      </div>

      <div className="col-md-12 px-0">
        <SectionPanelized {...props} />
      </div>

      <div className="row">
        <div className="col-md-6">
          <MaterialType {...props} options={materialType} />
        </div>

        <div className="col-md-6">
          <FinishPlating {...props} options={finishPlating} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <CopperWeight {...props} innerOptions={cooperInner} outerOptions={cooperOuter} template="two-cols" />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <Soldermask {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <SilkScreenSides {...props} />
        </div>
        <div className="col-md-6">
          <SilkScreenColor {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <ViaFill {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 col-lg-12 col-xl-6">
          <ControlledImpedance {...props} />
        </div>
        <div className="col-md-6 col-lg-12 col-xl-6">
          <Castellation {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 col-lg-12 col-xl-6">
          <Countersink {...props} />
        </div>
        <div className="col-md-6 col-lg-12 col-xl-6">
          <Counterbore {...props} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 col-lg-12 col-xl-6">
          <BlindSlot {...props} />
        </div>
      </div>

      <div className="row">
        <div className="col-12 py-3">
          <div className="card">
            <div className="card-header">
              CUSTOM SPECS (CANNOT QUOTE ONLINE)
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 col-lg-12 col-xl-6">
                  <BlindBuriedHole {...props} label="Blind & Buried Vias" tooltip={null} />
                </div>
                <div className="col-md-6 col-lg-12 col-xl-6">
                  <ControlledDielectric {...props} tooltip={null} />
                </div>
                <div className="col-md-6 col-lg-12 col-xl-6">
                  <MinTraceSpace {...props} />
                </div>
                <div className="col-md-6 col-lg-12 col-xl-6">
                  <SmallestHoleSize {...props} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <SpecialInstruction {...props} />
        </div>
      </div>

      <div>
        <SupplierSelected {...props} />
        <ProductQuoteNumber {...props} />
        <ParametersChanged {...props} />
        <CombinedSelected {...props} />
        <RowSelected {...props} />
        <ColumnSelected {...props} />
        <VendorSelected {...props} />
        <QuoteConfigKey {...props} />
        <EmailHidden {...props} />
        <input type="hidden" name="quantity" value="1" />
      </div>
    </div>
  );
};

export default Parameters;
